import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowUp, faRedo, faStop } from '@fortawesome/pro-solid-svg-icons';

import { MessageStream } from '../Agent';
import { useMoments } from './MomentsContext';

import './MomentsBuilder.scss';

// The conversation rail: transcript stream, composer, and the idea-chip empty
// state for a fresh moment. Polling and run state live in MomentsProvider.
const MomentsBuilder = () => {
  const {
    threadRef,
    messages,
    moment,
    isRunning,
    threadLoading,
    sending,
    actionBusy,
    stopping,
    input,
    composerRef,
    showMoment,
    setInput: onInputChange,
    send: onSend,
    stop: onStop,
    retry: onRetry,
    backToGallery: onBack
  } = useMoments();

  const streamFetchInfo = useMemo(() => ({ hasFetchedInitial: !threadLoading, isFetching: threadLoading, error: null }), [threadLoading]);

  const streamMessages = useMemo(
    () => (isRunning ? [...messages, { id: '_live', role: 'autopilot', message: '', _isStreaming: true, metadata: {} }] : messages),
    [messages, isRunning]
  );

  const onComposerKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      // The visible button has swapped to Stop while a build runs — Enter
      // must match it, not race the server into a BUILD_IN_PROGRESS 409.
      if (isRunning || sending) return;
      onSend(input);
    }
  };

  return (
    <div className='msa-chat-pane'>
      <div className='msa-chat-header'>
        <button className='msa-back-btn' onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> All Moments
        </button>
      </div>

      <div className='msa-conversation'>
        <MessageStream
          activeThread={threadRef ? { id: threadRef } : null}
          messages={streamMessages}
          isWorking={isRunning}
          fetchInfo={streamFetchInfo}
        />
        {threadLoading && messages.length === 0 && (
          <div className='msa-thread-loading'>
            <span className='msa-spinner' />
          </div>
        )}
      </div>

      {!isRunning && moment && moment.status === 'failed' && (
        <div className='msa-retry-row'>
          <span>The last build didn't finish.</span>
          <button className='msa-retry-btn' onClick={onRetry} disabled={actionBusy}>
            <FontAwesomeIcon icon={faRedo} /> Retry build
          </button>
        </div>
      )}

      <div className='msa-composer'>
        <textarea
          ref={composerRef}
          value={input}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={onComposerKeyDown}
          placeholder={showMoment ? 'Refine the Moment — "swap the heels for flats, add a rain layer"…' : 'Describe the moment you are shopping for…'}
          rows={1}
          disabled={sending}
        />
        {isRunning ? (
          <button className='msa-send-btn msa-stop-btn' onClick={onStop} disabled={stopping} title={stopping ? 'Stopping…' : 'Stop the build'}>
            {stopping ? <span className='msa-spinner msa-spinner-light' /> : <FontAwesomeIcon icon={faStop} />}
          </button>
        ) : (
          <button className='msa-send-btn' onClick={() => onSend(input)} disabled={!input.trim() || sending} title='Send'>
            <FontAwesomeIcon icon={faArrowUp} />
          </button>
        )}
      </div>
    </div>
  );
};

export default MomentsBuilder;
